import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { GAMES } from "../../data/games";
import { diagnoseGrid } from "../../utils/lottery";
import { ConformityGauge, scoreLevel } from "../ConformityGauge";
import { Button } from "../ui/button";
import { CheckCircle2, AlertTriangle, XCircle, RotateCcw, Target, Star } from "lucide-react";

const ICON = {
  pass: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
  warn: <AlertTriangle className="w-5 h-5 text-amber-500" />,
  fail: <XCircle className="w-5 h-5 text-rose-500" />,
};
const BG = { pass: "bg-emerald-50 border-emerald-200", warn: "bg-amber-50 border-amber-200", fail: "bg-rose-50 border-rose-200" };

const BONUS_GRADIENT = {
  chance: "linear-gradient(135deg, #EF4444, #991B1B)",
  star: "linear-gradient(135deg, #F59E0B, #B45309)",
  dream: "linear-gradient(135deg, #9333EA, #581C87)",
};

export const AnalyzerTab = ({ game, draws }) => {
  const cfg = GAMES[game];
  const need = cfg.kenoMode ? 10 : cfg.pick;
  const [selected, setSelected] = useState([]);
  const [selectedBonus, setSelectedBonus] = useState([]);

  const toggle = (n) => {
    setSelected((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : prev.length >= need ? prev : [...prev, n]));
  };

  const toggleBonus = (n) => {
    setSelectedBonus((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : prev.length >= cfg.bonusPick ? prev : [...prev, n]));
  };

  const bonusGrad = BONUS_GRADIENT[cfg.bonusType] || `linear-gradient(135deg, ${cfg.primary}, ${cfg.secondary})`;
  const bonusComplete = cfg.bonusPick === 0 || selectedBonus.length === cfg.bonusPick;

  const diag = useMemo(() => {
    if (selected.length < 2) return null;
    return diagnoseGrid([...selected].sort((a, b) => a - b), cfg, draws);
  }, [selected, cfg, draws]);

  const verdict = diag ? scoreLevel(diag.score) : null;
  const complete = selected.length === need;

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <Target className="w-5 h-5" style={{ color: cfg.primary }} /> Cochez vos {need} numéros
            </h3>
            <span className="text-sm font-bold" style={{ color: cfg.primary }}>{selected.length}/{need}</span>
          </div>
          <div className="grid grid-cols-7 sm:grid-cols-10 gap-1.5">
            {Array.from({ length: cfg.max }, (_, i) => i + 1).map((n) => {
              const on = selected.includes(n);
              return (
                <button key={n} onClick={() => toggle(n)} data-testid={`analyzer-toggle-${n}`}
                  className={`aspect-square rounded-full flex items-center justify-center font-bold text-sm border transition-all ${on ? "text-white scale-105 shadow-md border-transparent" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                  style={on ? { background: `linear-gradient(135deg, ${cfg.primary}, ${cfg.secondary})` } : {}}>
                  {n}
                </button>
              );
            })}
          </div>
          {cfg.bonusPick > 0 && (
            <div className="mt-5 pt-5 border-t border-slate-100">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                  <Star className="w-4 h-4" style={{ color: cfg.secondary }} /> {cfg.bonusName}
                </h4>
                <span className="text-sm font-bold" style={{ color: cfg.primary }}>{selectedBonus.length}/{cfg.bonusPick}</span>
              </div>
              <div className="grid grid-cols-7 sm:grid-cols-10 gap-1.5">
                {Array.from({ length: cfg.bonusMax }, (_, i) => i + 1).map((n) => {
                  const on = selectedBonus.includes(n);
                  return (
                    <button key={n} onClick={() => toggleBonus(n)} data-testid={`analyzer-bonus-toggle-${n}`}
                      className={`aspect-square rounded-full flex items-center justify-center font-bold text-sm border transition-all ${on ? "text-white scale-105 shadow-md border-transparent" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                      style={on ? { background: bonusGrad } : {}}>
                      {n}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <Button onClick={() => { setSelected([]); setSelectedBonus([]); }} variant="outline" className="mt-4 rounded-full font-bold" data-testid="analyzer-reset">
            <RotateCcw className="w-4 h-4 mr-1" /> Réinitialiser
          </Button>
        </div>
      </div>

      <div className="lg:col-span-2">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm sticky top-32" data-testid="analyzer-diagnostic-panel">
          <h3 className="font-bold text-slate-900 mb-4">Diagnostic FDJ</h3>
          {!diag ? (
            <p className="text-slate-400 text-sm py-8 text-center">Sélectionnez au moins 2 numéros pour lancer le diagnostic.</p>
          ) : (
            <>
              <div className="flex flex-col items-center mb-5">
                <ConformityGauge score={diag.score} size={110} testId="analyzer-score-gauge" />
                <span className={`mt-2 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider border ${verdict.badge}`} data-testid="analyzer-verdict">
                  {complete && bonusComplete ? verdict.label : `Grille incomplète (${selected.length}/${need}${cfg.bonusPick ? ` + ${selectedBonus.length}/${cfg.bonusPick}` : ""})`}
                </span>
              </div>
              {cfg.bonusPick > 0 && (
                <div className="flex items-center justify-center gap-2 mb-5 -mt-2" data-testid="analyzer-bonus-summary">
                  <span className="text-[10px] font-bold uppercase text-slate-400">{cfg.bonusName}</span>
                  {selectedBonus.length === 0 ? (
                    <span className="text-xs text-slate-400 italic">à sélectionner</span>
                  ) : (
                    selectedBonus.slice().sort((a, b) => a - b).map((b) => (
                      <span key={b} className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-black shadow" style={{ background: bonusGrad }}>{b}</span>
                    ))
                  )}
                </div>
              )}
              <div className="space-y-2">
                {diag.rules.map((r) => (
                  <motion.div key={r.key} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }}
                    className={`flex items-start gap-3 rounded-xl border p-3 ${BG[r.status]}`} data-testid={`analyzer-rule-${r.key}`}>
                    {ICON[r.status]}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-sm text-slate-800">{r.label}</span>
                        <span className="font-mono font-bold text-xs text-slate-600 whitespace-nowrap">{r.value}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{r.detail}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
